/* eslint-disable no-shadow */
import FetchStatus from "@/constants/fetch-status/FetchStatus";
import Vuetify from "@/plugins/vuetify";
import { httpV2 } from "@/api/index";
import WDService from "@/services/wind/diagnostic/WindDiagnosticService";
import { ChartLinesColorsDark, SetLayoutLinesColor } from "@/components/charts/ChartStyles";
import {
	GET_GEARBOX_BROKEN_PINION_STAGE3_DIAGNOSTIC,
	GET_GEARBOX_BROKEN_PINION_STAGE3_DIAGNOSTIC_ERROR,
	GET_GEARBOX_BROKEN_PINION_STAGE3_DIAGNOSTIC_SUCCESS,
	GET_GEARBOX_BROKEN_PINION_STAGE2_DIAGNOSTIC,
	GET_GEARBOX_BROKEN_PINION_STAGE2_DIAGNOSTIC_ERROR,
	GET_GEARBOX_BROKEN_PINION_STAGE2_DIAGNOSTIC_SUCCESS,

} from "@/stores/actions/GearBoxBrokenPinionDiagnostic";

import SortingService from "@/services/common/SortingService";

const state = {
	frequencyenvelopeChartData: [],
	orderenvelopeChartData:     [],
	frequencyenvelopeLayout:    {},
	orderenvelopeLayout:        {},

	frequencySpectrumChartData: [],
	orderSpectrumChartData:     [],
	frequencySpectrumLayouts:   {},
	orderSpectrumLayouts:       {},

	concernFrequencies: [],
	concernOrders:      [],

	status: FetchStatus.Init,
	error:  {},
};

/**
* @summary Cria as anotações e atribui na váriavel layout.
* @param {Array} orders Array com as posiçoes do eixo x das anotações.
*/
const CreateAnnotations = (orders) =>
{
	const shapes = [];
	for (let i = 0; i < orders.length; i++)
	{
		const shape = {
			yref: "paper",
			type: "line",
			x0:   orders[i],
			x1:   orders[i],
			y0:   0, // Representa 0%
			y1:   1, // Representa 100%
			line: {
				color: ChartLinesColorsDark[0],
				width: 2,
				dash:  "dot",
			},
			visible: false,
		};
		shapes.push(shape);
	}
	return shapes;
};

/**
* @summary Cria a anotação específica para ordem de engrenamento.
* @param {Array} gearingOrder Ordem de engrenamento.
*/
const CreateGearingOrderAnnotation = (gearingOrder) =>
{
	const shape = {
		yref: "paper",
		type: "line",
		x0:   gearingOrder,
		x1:   gearingOrder,
		y0:   0, // Representa 0%
		y1:   1, // Representa 100%
		line: {
			color: ChartLinesColorsDark[2],
			width: 2,
			dash:  "dot",
		},
		visible: true,
	};
	return shape;
};

/**
* @summary Cria os textos para as anotações
* @param {Array - Numbers} orders - Posições das anotações no eixo Y.
* @param {String} componentName - Nome do componente.
* @param {String} darkMode - booleano que indica se o sistema está com o dark mode ativado.
*/
const CreateAnnotationText = (orders, componentName, darkMode, gmf) =>
{
	const textColor = darkMode ? Vuetify.framework.theme.themes.dark.secondary :
		Vuetify.framework.theme.themes.light.secondary;

	const annotations = [];
	let positiveValuesCount = 0;
	let negativeValuesCount = orders.length / 2;

	for (let i = 0; i < orders.length; i++)
	{
		let text = "";
		if (orders[i] > gmf)
		{
			text = `${positiveValuesCount + 1}x ${componentName}`;
			positiveValuesCount += 1;
		}
		else
		{
			text = `${negativeValuesCount}x ${componentName}`;
			negativeValuesCount -= 1;
		}

		const annotation = {
			x:          orders[i],
			y:          0.8, // 80 % do valor em Y
			yref:       "paper",
			mode:       "text",
			showlegend: false,
			text,
			showarrow:  false,
			visible:    false,
			textangle:  270,
			align:      "right",
			yanchor:    "top",
			xshift:     10,
			yshift:     30,
			font:       { color: textColor },
		};
		annotations.push(annotation);
	}
	return annotations;
};

/**
* @summary Cria os textos para a anotação da ordem de engrenamento.
* @param {Array - Numbers} gearingOrder - Ordem de engrenamento.
* @param {String} darkMode - booleano que indica se o sistema está com o dark mode ativado.
*/
const CreateGearingOrderAnnotationText = (gearingOrder, darkMode) =>
{
	const textColor = darkMode ? Vuetify.framework.theme.themes.dark.secondary :
		Vuetify.framework.theme.themes.light.secondary;

	const annotation = {
		x:          gearingOrder,
		y:          0.8, // 80 % do valor em Y
		yref:       "paper",
		mode:       "text",
		showlegend: false,
		text:       "GMF",
		showarrow:  false,
		visible:    true,
		textangle:  270,
		align:      "right",
		yanchor:    "top",
		xshift:     10,
		yshift:     30,
		font:       { color: textColor },
	};
	return annotation;
};

const actions = {
	[GET_GEARBOX_BROKEN_PINION_STAGE3_DIAGNOSTIC]: ({ commit }, parameters) =>
	{
		commit(GET_GEARBOX_BROKEN_PINION_STAGE3_DIAGNOSTIC);

		httpV2.get(`/WindTurbinesDiagnostic/${parameters.resultSetId}/${parameters.location}
		/GearboxStage3BrokenTeeth/Charts`)
			.then((resp) =>
			{
				SortingService.SortListAlphabeticallyByName(resp.data.envelopeCharts);

				for (let i = 0; i < resp.data.fullSpectrumCharts.length; i++)
				{
					const charts = resp.data.fullSpectrumCharts[i].charts;
					
					SortingService.SortListAlphabeticallyByName(charts);
				}

				const spectrumCharts = resp.data.fullSpectrumCharts;
				const envelopeCharts = resp.data.envelopeCharts;

				const concernFrequenciesEnvelope = envelopeCharts[0].concernFrequencies;
				const concernOrdersEnvelope = envelopeCharts[0].concernOrders;

				const response = {
					orderChartData:          { envelopeChart: [], fullSpectrumCharts: [] },
					frequencyChartData:      { envelopeChart: [], fullSpectrumCharts: [] },
					spectrumOrderLayout:     [],
					spectrumFrequencyLayout: [],
					envelopeOrderLayout:     {},
					envelopeFrequencyLayout: {},
					gearMeshFrequency:       0,
					gearMeshOrder:           0,
				};

				response.orderChartData.envelopeChart = WDService.CreateChart(envelopeCharts, true);
				response.frequencyChartData.envelopeChart = WDService.CreateChart(envelopeCharts, false);

				for (let i = 0; i < spectrumCharts.length; i++)
				{
					const charts = spectrumCharts[i].charts;

					const orderChart = WDService.CreateChart(charts, true);
					const frequencyChart = WDService.CreateChart(charts, false);

					response.orderChartData.fullSpectrumCharts.push(orderChart);
					response.frequencyChartData.fullSpectrumCharts.push(frequencyChart);
				}

				response.gearMeshFrequency = resp.data.gearMeshFrequency;
				response.gearMeshOrder = resp.data.gearMeshOrder;

				response.envelopeOrderLayout = SetLayoutLinesColor(WDService.OrderLayoutModel(), parameters.isDarkMode);
				response.envelopeFrequencyLayout = SetLayoutLinesColor(WDService.FrequencyLayoutModel(), parameters.isDarkMode);

				response.envelopeOrderLayout.shapes = WDService.CreateShapes(concernOrdersEnvelope);
				response.envelopeOrderLayout.annotations =
					WDService.CreateAnnotationText(concernOrdersEnvelope, parameters.isDarkMode, parameters.locationName);

				response.envelopeFrequencyLayout.shapes = WDService.CreateShapes(concernFrequenciesEnvelope);
				response.envelopeFrequencyLayout.annotations =
					WDService.CreateAnnotationText(concernFrequenciesEnvelope, parameters.isDarkMode, parameters.locationName);

				for (let i = 0; i < response.orderChartData.fullSpectrumCharts.length; i++)
				{
					const { concernFrequencies } = spectrumCharts[i].charts[0];
					const { concernOrders } = spectrumCharts[i].charts[0];

					const orderLayout = SetLayoutLinesColor(WDService.OrderLayoutModel(), parameters.isDarkMode);
					const frequencyLayout = SetLayoutLinesColor(WDService.FrequencyLayoutModel(), parameters.isDarkMode);

					const gmfHarmonic = response.gearMeshFrequency * (i + 1);
					const gmoHarmonic = response.gearMeshOrder * (i + 1);

					const orderShapes = CreateAnnotations(concernOrders);
					const orderAnnotations =
						CreateAnnotationText(concernOrders, parameters.locationName, parameters.isDarkMode, gmoHarmonic);

					const frequencyShapes = CreateAnnotations(concernFrequencies);
					const frequencyAnnotations =
						CreateAnnotationText(concernFrequencies, parameters.locationName, parameters.isDarkMode, gmfHarmonic);

					orderShapes.push(CreateGearingOrderAnnotation(gmoHarmonic));
					orderAnnotations.push(CreateGearingOrderAnnotationText(gmoHarmonic, parameters.isDarkMode));

					frequencyShapes.push(CreateGearingOrderAnnotation(gmfHarmonic));
					frequencyAnnotations
						.push(CreateGearingOrderAnnotationText(gmfHarmonic, parameters.isDarkMode));

					orderLayout.shapes = orderShapes;
					orderLayout.annotations = orderAnnotations;

					orderLayout.legend.font.size = 14;
					orderLayout.legend.y = 2;

					frequencyLayout.shapes = frequencyShapes;
					frequencyLayout.annotations = frequencyAnnotations;
					
					orderLayout.legend.font.size = 14;
					orderLayout.legend.y = 2;

					response.spectrumOrderLayout.push(orderLayout);
					response.spectrumFrequencyLayout.push(frequencyLayout);
				}

				commit(GET_GEARBOX_BROKEN_PINION_STAGE3_DIAGNOSTIC_SUCCESS, response);
			})
			.catch((error) =>
			{
				commit(GET_GEARBOX_BROKEN_PINION_STAGE3_DIAGNOSTIC_ERROR, error.response);
			});
	},

	[GET_GEARBOX_BROKEN_PINION_STAGE2_DIAGNOSTIC]: ({ commit }, parameters) =>
	{
		commit(GET_GEARBOX_BROKEN_PINION_STAGE2_DIAGNOSTIC);

		httpV2.get(`/WindTurbinesDiagnostic/${parameters.resultSetId}/${parameters.location}
		/GearboxStage2BrokenTeeth/Charts`)
		 .then((resp) =>
		 {
				SortingService.SortListAlphabeticallyByName(resp.data.envelopeCharts);

				for (let i = 0; i < resp.data.fullSpectrumCharts.length; i++)
				{
					const charts = resp.data.fullSpectrumCharts[i].charts;
				
					SortingService.SortListAlphabeticallyByName(charts);
				}

				const spectrumCharts = resp.data.fullSpectrumCharts;
				const envelopeCharts = resp.data.envelopeCharts;

				const concernFrequenciesEnvelope = envelopeCharts[0].concernFrequencies;
				const concernOrdersEnvelope = envelopeCharts[0].concernOrders;

				const response = {
					orderChartData:          { envelopeChart: [], fullSpectrumCharts: [] },
					frequencyChartData:      { envelopeChart: [], fullSpectrumCharts: [] },
					spectrumOrderLayout:     [],
					spectrumFrequencyLayout: [],
					envelopeOrderLayout:     {},
					envelopeFrequencyLayout: {},
					gearMeshFrequency:       0,
					gearMeshOrder:           0,
				};

				response.orderChartData.envelopeChart = WDService.CreateChart(envelopeCharts, true);
				response.frequencyChartData.envelopeChart = WDService.CreateChart(envelopeCharts, false);

				for (let i = 0; i < spectrumCharts.length; i++)
				{
					const charts = spectrumCharts[i].charts;

					const orderChart = WDService.CreateChart(charts, true);
					const frequencyChart = WDService.CreateChart(charts, false);

					response.orderChartData.fullSpectrumCharts.push(orderChart);
					response.frequencyChartData.fullSpectrumCharts.push(frequencyChart);
				}

				response.gearMeshFrequency = resp.data.gearMeshFrequency;
				response.gearMeshOrder = resp.data.gearMeshOrder;

				response.envelopeOrderLayout = SetLayoutLinesColor(WDService.OrderLayoutModel(), parameters.isDarkMode);
				response.envelopeFrequencyLayout = SetLayoutLinesColor(WDService.FrequencyLayoutModel(), parameters.isDarkMode);

				response.envelopeOrderLayout.shapes = WDService.CreateShapes(concernOrdersEnvelope);
				response.envelopeOrderLayout.annotations =
					WDService.CreateAnnotationText(concernOrdersEnvelope, parameters.isDarkMode, parameters.locationName);

				response.envelopeFrequencyLayout.shapes = WDService.CreateShapes(concernFrequenciesEnvelope);
				response.envelopeFrequencyLayout.annotations =
					WDService.CreateAnnotationText(concernFrequenciesEnvelope, parameters.isDarkMode, parameters.locationName);

				for (let i = 0; i < response.orderChartData.fullSpectrumCharts.length; i++)
				{
					const { concernFrequencies } = spectrumCharts[i].charts[0];
					const { concernOrders } = spectrumCharts[i].charts[0];

					const orderLayout = SetLayoutLinesColor(WDService.OrderLayoutModel(), parameters.isDarkMode);
					const frequencyLayout = SetLayoutLinesColor(WDService.FrequencyLayoutModel(), parameters.isDarkMode);

					const gmfHarmonic = response.gearMeshFrequency * (i + 1);
					const gmoHarmonic = response.gearMeshOrder * (i + 1);

					const orderShapes = CreateAnnotations(concernOrders);
					const orderAnnotations =
						CreateAnnotationText(concernOrders, parameters.locationName, parameters.isDarkMode, gmoHarmonic);
					const frequencyShapes = CreateAnnotations(concernFrequencies);
					const frequencyAnnotations =
						CreateAnnotationText(concernFrequencies, parameters.locationName, parameters.isDarkMode, gmfHarmonic);

					orderShapes.push(CreateGearingOrderAnnotation(gmoHarmonic));
					orderAnnotations.push(CreateGearingOrderAnnotationText(gmoHarmonic, parameters.isDarkMode));

					frequencyShapes.push(CreateGearingOrderAnnotation(gmfHarmonic));
					frequencyAnnotations
						.push(CreateGearingOrderAnnotationText(gmfHarmonic, parameters.isDarkMode));

					orderLayout.shapes = orderShapes;
					orderLayout.annotations = orderAnnotations;

					orderLayout.legend.font.size = 14;
					orderLayout.legend.y = 2;

					frequencyLayout.shapes = frequencyShapes;
					frequencyLayout.annotations = frequencyAnnotations;
					
					orderLayout.legend.font.size = 14;
					orderLayout.legend.y = 2;

					response.spectrumOrderLayout.push(orderLayout);
					response.spectrumFrequencyLayout.push(frequencyLayout);
				}

			 commit(GET_GEARBOX_BROKEN_PINION_STAGE2_DIAGNOSTIC_SUCCESS, response);
		 })
		 .catch((error) =>
		 {
			 commit(GET_GEARBOX_BROKEN_PINION_STAGE2_DIAGNOSTIC_ERROR, error.response);
		 });
	},
};

const mutations = {
	[GET_GEARBOX_BROKEN_PINION_STAGE3_DIAGNOSTIC]: (state) =>
	{
		state.status = FetchStatus.Loading;
	},
	[GET_GEARBOX_BROKEN_PINION_STAGE3_DIAGNOSTIC_SUCCESS]: (state, resp) =>
	{
		state.frequencyenvelopeChartData = resp.frequencyChartData.envelopeChart;
		state.orderenvelopeChartData = resp.orderChartData.envelopeChart;
		state.frequencyenvelopeLayout = resp.envelopeFrequencyLayout;
		state.orderenvelopeLayout = resp.envelopeOrderLayout;

		state.frequencySpectrumChartData = resp.frequencyChartData.fullSpectrumCharts;
		state.orderSpectrumChartData = resp.orderChartData.fullSpectrumCharts;
		state.frequencySpectrumLayouts = resp.spectrumFrequencyLayout;
		state.orderSpectrumLayouts = resp.spectrumOrderLayout;
		state.status = FetchStatus.Loaded;
	},
	[GET_GEARBOX_BROKEN_PINION_STAGE3_DIAGNOSTIC_ERROR]: (state, error) =>
	{
		state.error = error.response;
		state.status = FetchStatus.Error;
	},
	[GET_GEARBOX_BROKEN_PINION_STAGE2_DIAGNOSTIC]: (state) =>
	{
		state.status = FetchStatus.Loading;
	},
	[GET_GEARBOX_BROKEN_PINION_STAGE2_DIAGNOSTIC_SUCCESS]: (state, resp) =>
	{
		state.frequencyenvelopeChartData = resp.frequencyChartData.envelopeChart;
		state.orderenvelopeChartData = resp.orderChartData.envelopeChart;
		state.frequencyenvelopeLayout = resp.envelopeFrequencyLayout;
		state.orderenvelopeLayout = resp.envelopeOrderLayout;

		state.frequencySpectrumChartData = resp.frequencyChartData.fullSpectrumCharts;
		state.orderSpectrumChartData = resp.orderChartData.fullSpectrumCharts;
		state.frequencySpectrumLayouts = resp.spectrumFrequencyLayout;
		state.orderSpectrumLayouts = resp.spectrumOrderLayout;
		state.status = FetchStatus.Loaded;
	},
	[GET_GEARBOX_BROKEN_PINION_STAGE2_DIAGNOSTIC_ERROR]: (state, error) =>
	{
		state.error = error.response;
		state.status = FetchStatus.Error;
	},
};

export default {
	state,
	actions,
	mutations,
};
